import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import {
  parseWorkspaceAgentMarketingUrlParams,
  stripWorkspaceAgentMarketingQueryParams,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/parseWorkspaceAgentMarketingUrlParams';

/**
 * When the workspace definitively lacks agent access, strip marketing deep-link params so the URL
 * does not keep a dead `agentUseCase`. Waits until access is resolved so the provider can consume
 * the param first when access is granted.
 */
export const usePrivateStripWorkspaceAgentMarketingParamsWithoutAccess = () => {
  const router = useRouter();
  const { isWorkspaceAgentAccessResolved, isWorkspaceAgentPanelProviderEnabled } = useIsWorkspaceAgentPanelAvailable();
  const shouldStripMarketingParams = isWorkspaceAgentAccessResolved && !isWorkspaceAgentPanelProviderEnabled;
  const hasStrippedMarketingParamsRef = useRef<boolean>(false);

  useEffect(() => {
    if (!shouldStripMarketingParams || !router.isReady || hasStrippedMarketingParamsRef.current) {
      return;
    }

    const { hasAgentUseCaseParam } = parseWorkspaceAgentMarketingUrlParams(
      typeof window === 'undefined' ? '' : window.location.search,
    );

    if (!hasAgentUseCaseParam) {
      return;
    }

    hasStrippedMarketingParamsRef.current = true;
    stripWorkspaceAgentMarketingQueryParams('replace');
  }, [router.isReady, shouldStripMarketingParams]);
};
